/**
 * Where an EMAIL_LLM draft's ship-from warehouse came from (kanban b5ba3619,
 * item history kanban 29601f9c).
 *
 * Three sources, mirrored from the backend audit trail on
 * `orders.llm_extraction_data`:
 *   1. Merlin proposed the code (`warehouse_code` + `warehouse_code_evidence`).
 *   2. The bridge filled it from the customer's unanimous will-call invoice
 *      history (`pipeline_filled_fields[field === 'warehouse_code']` with
 *      reason `customer_will_call_history_unanimous`, counts on
 *      `will_call_warehouse_history`).
 *   3. The bridge filled it from the customer's unanimous shipped history of
 *      the ordered items (same row, reason `customer_item_history_unanimous`,
 *      per-item counts on `item_warehouse_history`).
 *
 * `text` is the one-line hint printed next to the field; `title` is the full
 * sentence for hover. The hint is shown only while the order still carries
 * that exact code — the moment a CSR changes the warehouse, the provenance no
 * longer describes the value on screen (same rule as the requested-ship-date
 * "From PO" hint).
 */
import type { LlmExtractionData, PipelineFieldAuditRow, WarehouseHistoryRow } from '../types/orders';

export interface WarehouseProvenance {
  source: 'merlin' | 'will_call_history' | 'item_history';
  /** Short, one-line, CSR-readable hint. */
  text: string;
  /** Full sentence for the hover title. */
  title: string;
}

const ITEM_HISTORY_REASON = 'customer_item_history_unanimous';
const MAX_INLINE = 90;

function normalize(code: string | null | undefined): string {
  return (code || '000').trim().toUpperCase();
}

function clip(text: string): string {
  return text.length > MAX_INLINE ? `${text.slice(0, MAX_INLINE - 1)}…` : text;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function willCallFill(filled: PipelineFieldAuditRow, current: string, extraction: LlmExtractionData): WarehouseProvenance {
  const history = extraction.will_call_warehouse_history || [];
  const match = history.find((h) => h && normalize(h.warehouse_code) === current);
  const count = match && typeof match.invoices === 'number' ? match.invoices : null;
  const text = count !== null
    ? `From will-call history: ${plural(count, 'invoice')} shipped from ${filled.value}`
    : `From will-call history (${filled.value})`;
  return { source: 'will_call_history', text, title: filled.detail || text };
}

function itemHistoryFill(filled: PipelineFieldAuditRow, current: string, extraction: LlmExtractionData): WarehouseProvenance {
  // The one-liner sits in a narrow column next to the Warehouse dropdown, so
  // it carries the count only; the items are in the lines below and the full
  // sentence (with the item codes) is the hover title.
  const history = extraction.item_warehouse_history || {};
  let lines = 0;
  for (const rows of Object.values(history)) {
    for (const row of rows as WarehouseHistoryRow[]) {
      if (row && normalize(row.warehouse_code) === current && typeof row.invoices === 'number') {
        lines += row.invoices;
      }
    }
  }
  const text = lines
    ? `From shipped history: ${plural(lines, 'invoice line')} from ${filled.value}`
    : `From shipped history (${filled.value})`;
  return { source: 'item_history', text, title: filled.detail || text };
}

export function warehouseProvenance(
  currentWarehouseCode: string | null | undefined,
  extraction: LlmExtractionData | null | undefined,
): WarehouseProvenance | null {
  if (!extraction) return null;
  const current = normalize(currentWarehouseCode);

  const proposed = extraction.warehouse_code ? normalize(extraction.warehouse_code) : null;
  if (proposed && proposed === current) {
    const evidence = (extraction.warehouse_code_evidence || '').trim();
    const title = evidence ? `Merlin read this on the PO / in Sage history: “${evidence}”` : 'Proposed by Merlin';
    return {
      source: 'merlin',
      text: evidence ? clip(`From PO: “${evidence}”`) : 'Proposed by Merlin',
      title,
    };
  }

  const filled = (extraction.pipeline_filled_fields || []).find(
    (row) => row && row.field === 'warehouse_code' && normalize(row.value) === current,
  );
  if (!filled) return null;
  return filled.reason === ITEM_HISTORY_REASON
    ? itemHistoryFill(filled, current, extraction)
    : willCallFill(filled, current, extraction);
}
